import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";

function fmtPrice(q) {
  if (q.kind === "fx") return q.price.toFixed(4);
  if (q.kind === "crypto" && q.price > 100) return q.price.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (q.price > 100) return q.price.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return q.price.toFixed(2);
}

function curSymbol(c) {
  if (c === "GBP") return "£";
  if (c === "EUR") return "€";
  if (c === "JPY") return "¥";
  return "$";
}

export function MarketTicker() {
  const [quotes, setQuotes] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/markets/quotes");
      setQuotes(data.quotes || []);
      setUpdatedAt(data.updated_at);
    } catch (err) {
      console.warn("MarketTicker quotes fetch failed:", err);
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => clearInterval(timer.current);
  }, []);

  if (!quotes.length) {
    return (
      <div className="bg-black text-white py-2 px-4 text-[11px] font-mono-ed uppercase tracking-[0.25em] text-neutral-500 border-y border-white/10" data-testid="market-ticker-loading">
        Markets · Loading live quotes…
      </div>
    );
  }

  // Duplicate the array so the loop is seamless
  const loop = [...quotes, ...quotes];

  return (
    <div
      className="bg-black text-white border-y border-white/10 overflow-hidden relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="market-ticker"
    >
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center px-4 bg-[var(--hinton-red)] text-white text-[10px] font-bold uppercase tracking-[0.25em]">
        <span className="hidden sm:inline">Hinton</span><span className="sm:ml-2">Markets</span>
        <span className="ml-3 w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
      </div>
      <div className="pl-[150px] sm:pl-[170px]">
        <div
          className="marquee-track py-2.5 will-change-transform"
          style={{ animationPlayState: paused ? "paused" : "running", animationDuration: "120s" }}
        >
          {loop.map((q, i) => {
            const up = (q.change || 0) >= 0;
            const sign = up ? "+" : "";
            const color = up ? "text-emerald-400" : "text-[#FF6B6B]";
            const cs = q.kind === "fx" ? "" : curSymbol(q.currency);
            const hasPrice = q.price !== null && q.price !== undefined;
            return (
              <span key={`${q.symbol}-${i}`} className="px-5 text-[12px] font-mono-ed uppercase tracking-[0.15em] whitespace-nowrap inline-flex items-center gap-2">
                {q.is_new && (
                  <span className="bg-[var(--hinton-red)] text-white px-1.5 py-0.5 text-[9px] tracking-[0.2em] font-bold animate-pulse" data-testid={`market-new-${q.symbol}`}>
                    {q.status === "pre-ipo" ? "IPO Today" : "New IPO"}
                  </span>
                )}
                <span className="text-neutral-400">{q.label}</span>
                {hasPrice ? (
                  <>
                    <span className="text-white font-semibold">{cs}{fmtPrice(q)}</span>
                    <span className={color}>{sign}{(q.change_pct || 0).toFixed(2)}%</span>
                  </>
                ) : (
                  <span className="text-neutral-500 italic">Awaiting first trade</span>
                )}
                <span className="text-neutral-700 ml-1">|</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
